import { QrCodeVisitWeb } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { toLower } from 'lodash';
import { useMemo, useState } from 'react';
import { useDebouncedValue } from 'v2/hooks/useDebouncedValue/useDebouncedValue';
import { useSearch } from 'v2/hooks/useSearch/useSearch';
import { formatDate } from 'v2/utils/dateTime';

interface Params {
  visits: QrCodeVisitWeb[];
}

const VISIT_SEARCH_KEYS = ['name', 'email', 'host.name', 'host.email'];
const SEARCH_DEBOUNCE_MS = 300;

// Dates must only ever match as an exact substring, never fuzzily - a fuzzy score could
// otherwise surface visits on a nearby date that the typed text isn't actually part of.
const getVisitDateStrings = (visit: QrCodeVisitWeb): string[] => [
  formatDate(visit.datetime, 'dateOnly'),
  formatDate(visit.datetime, 'dateOnlyLong')
];

export const useVisitsSearch = ({ visits }: Params) => {
  const { translation } = useTranslation();

  const [filterStr, setFilterStr] = useState('');
  const debouncedSearch = useDebouncedValue(filterStr, SEARCH_DEBOUNCE_MS).trim().toLowerCase();

  const textMatches = useSearch({ data: visits, search: filterStr, keys: VISIT_SEARCH_KEYS });

  const dateMatches = useMemo(
    () =>
      debouncedSearch
        ? visits.filter((visit) =>
            getVisitDateStrings(visit).some((date) => date.toLowerCase().includes(debouncedSearch))
          )
        : [],
    [visits, debouncedSearch]
  );

  // Two independent match sets, merged: fuzzy matches (name/email) first, then any
  // date-only matches appended, deduping visits caught by both criteria.
  const filteredVisits = useMemo(() => {
    if (!debouncedSearch) return visits;

    const textMatchIds = new Set(textMatches.map((visit) => visit.id));
    const additionalDateMatches = dateMatches.filter((visit) => !textMatchIds.has(visit.id));

    return [...textMatches, ...additionalDateMatches];
  }, [visits, debouncedSearch, textMatches, dateMatches]);

  const countCaption = `${filteredVisits.length} ${translation.of} ${visits.length} ${toLower(translation.visitors)}`;

  return {
    filterStr,
    setFilterStr,
    filteredVisits,
    sections: [],
    chips: [],
    countCaption
  };
};
